// View Team Users Modal Component

import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Search, Users as UsersIcon } from 'lucide-react';
import { userHasTeam } from '@/lib/utils';
import type { Team, User } from '@/types/teams';

interface ViewTeamUsersModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly teams: Team[];
  readonly allUsers: User[];
}

// Helper function to get user initials
const getUserInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Helper function to generate a consistent color based on user name
const getUserColor = (name: string): string => {
  const colors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
    '#14B8A6', '#EC4899', '#F97316', '#84CC16', '#06B6D4',
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

export function ViewTeamUsersModal({
  open,
  onOpenChange,
  teams,
  allUsers,
}: Readonly<ViewTeamUsersModalProps>) {
  const [searchQuery, setSearchQuery] = useState('');

  // Get the selected team (only one team should be selected for this modal)
  const selectedTeam = teams[0];

  // Get all users from the selected team
  const teamUsers = useMemo(() => {
    if (!selectedTeam) return [];
    return allUsers.filter(user => userHasTeam(user.teams, selectedTeam.id));
  }, [selectedTeam, allUsers]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return teamUsers;

    const query = searchQuery.toLowerCase();
    return teamUsers.filter(user =>
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  }, [teamUsers, searchQuery]);


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Users in {selectedTeam?.name || 'Team'}
          </DialogTitle>
          <DialogDescription>
            {teamUsers.length} {teamUsers.length === 1 ? 'user' : 'users'} in this team
          </DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Users List */}
        <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
          <div className="space-y-2">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <UsersIcon className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm">
                  {searchQuery ? 'No users found matching your search' : 'No users found'}
                </p>
              </div>
            ) : (
              filteredUsers.map((user) => {
                return (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                  >
                    {/* User Avatar */}
                    <div
                      className="h-10 w-10 rounded-md flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                      style={{ backgroundColor: getUserColor(user.name) }}
                    >
                      {getUserInitials(user.name)}
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{user.name}</span>
                        <Badge
                          variant={user.status === 'active' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {user.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
